import { useForm } from "react-hook-form";
import {
  useAddPostSubmissionNoteMutation,
  useVoidMedicationLogMutation,
} from "../../../services/api/medicationsApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { MedicationPostSubmissionNoteModel } from "../../../services/api/types/medications/models/MedicationPostSubmissionNoteModel";
import { MedicationReportModel } from "../../../services/api/types/medications/models/MedicationReportModel";

interface Props {
  onSuccess?: () => void | null;
}

export function useModifyMedicationLog(
  medication: MedicationReportModel | null,
  { onSuccess }: Props,
) {
  const { showNewToast, showErrorToast } = useDisplayToast();

  const [addPostSubmissionNote] = useAddPostSubmissionNoteMutation();

  const [voidMedicationLog, { isLoading: isVoidLoading }] =
    useVoidMedicationLogMutation();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<MedicationPostSubmissionNoteModel>({
    defaultValues: {
      note: "",
    },
  });

  const submit = handleSubmit(async (data) => {
    try {
      if (!medication) return;

      const body: MedicationPostSubmissionNoteModel = {
        medication_report_id: medication.id,
        note: data.note,
      };
      await addPostSubmissionNote(body).unwrap();

      if (onSuccess) onSuccess();

      showNewToast({
        title: "Note Submitted",
        message: "The note has been submitted successfully",
        type: "success",
      });
    } catch (error) {
      showErrorToast(error);
    }
  });

  const handleVoidMedicationLog = async () => {
    try {
      if (!medication) return;

      await voidMedicationLog({ medicationLogId: medication.id }).unwrap();

      if (onSuccess) onSuccess();

      showNewToast({
        title: "Medication Log Voided",
        message: "The medication log has been marked as voided successfully",
        type: "success",
      });
    } catch (error) {
      showErrorToast(error);
    }
  };

  return {
    control,
    submit,
    isSubmitting,
    handleVoidMedicationLog,
    isVoidLoading,
  };
}
